import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useGetIdentity } from '@refinedev/core';
import { useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridViewOutlined';
import TableRowsIcon from '@mui/icons-material/TableRowsOutlined';
import {
  createUnit,
  deleteUnit,
  listAdminUnits,
  updateUnit,
  type AdminUnit,
} from '../../lib/units-api';
import { listResidents, type PublicResident } from '../../lib/residents-api';
import { getUnitBilling, type UnitBilling } from '../../lib/ops-api';
import {
  buildUnitRows,
  filterCounts,
  groupByBlock,
  matchesFilter,
  matchesSearch,
  type UnitFilter,
  type UnitRow,
} from '../../lib/unit-directory';
import { extractApiError } from '../../lib/api-client';
import { FilterChips } from '../../components/FilterChips';
import { BlockMap, BlockMapLegend } from './BlockMap';
import { UnitDrawer } from './UnitDrawer';
import { UnitImportPanel } from './UnitImport';
import { useT } from '../../i18n';

interface Identity {
  permissions?: string[];
}

const UNIT_TYPES = ['HOUSE', 'APARTMENT', 'RUKO'] as const;
// The form offers lived-in or vacant only. OWNER_ABSENT still exists in the
// data; such a unit shows as occupied and saves as OCCUPIED once edited.
const OCCUPANCIES = ['OCCUPIED', 'VACANT'] as const;

const UNIT_TYPE_LABELS: Record<string, string> = {
  HOUSE: 'units.typeHouse',
  APARTMENT: 'units.typeApartment',
  RUKO: 'units.typeRuko',
};
const OCCUPANCY_LABELS: Record<string, string> = {
  OCCUPIED: 'units.occupiedChip',
  VACANT: 'units.vacantChip',
  OWNER_ABSENT: 'units.occupiedChip',
};

// Occupancy is descriptive only — nothing in the system branches on it. These
// help lines exist so two staff members entering units land on the same
// meaning, and so nobody mistakes "Vacant" for a billing exemption. The
// canonical definitions live in docs/02-schema.md.
const OCCUPANCY_HELP: Record<string, string> = {
  OCCUPIED: 'units.occHelpOccupied',
  VACANT: 'units.occHelpVacant',
  OWNER_ABSENT: 'units.occHelpOwnerAbsent',
};

const FILTERS: Array<{ key: UnitFilter; label: string }> = [
  { key: 'all', label: 'units.filterAll' },
  { key: 'OCCUPIED', label: 'units.occupiedChip' },
  { key: 'VACANT', label: 'units.vacantChip' },
  { key: 'unregistered', label: 'units.filterUnregistered' },
];

type View = 'map' | 'table';
const VIEW_KEY = 'residenone:units-view';

function savedView(): View {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'map';
  } catch {
    return 'map';
  }
}

export function UnitsListPage() {
  const t = useT();
  const [units, setUnits] = useState<AdminUnit[]>([]);
  const [residents, setResidents] = useState<PublicResident[] | null>(null);
  const [billing, setBilling] = useState<Map<string, UnitBilling> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminUnit | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AdminUnit | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminUnit | null>(null);
  // ?filter=unregistered (from the dashboard's adoption tile) opens pre-filtered.
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<UnitFilter>(() => {
    const f = searchParams.get('filter');
    return f === 'OCCUPIED' || f === 'VACANT' || f === 'unregistered' ? f : 'all';
  });
  const [query, setQuery] = useState('');
  const [block, setBlock] = useState('all');
  const [view, setView] = useState<View>(savedView);

  const { data: identity } = useGetIdentity<Identity>();
  const canManageResidents = (identity?.permissions ?? []).includes('can_manage_residents');

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteUnit(deleting.id);
      setInfo(t('units.deletedInfo', { label: deleting.label }));
      setDeleting(null);
      setSelected(null);
      refresh();
    } catch (e) {
      const { code, message } = extractApiError(e);
      if (code === 'ERR_NOT_FOUND') {
        // Already gone (concurrent delete / stale list) — treat as success.
        setInfo(t('units.alreadyRemoved'));
        setDeleting(null);
        setSelected(null);
        refresh();
      } else {
        // 409 blocker (has residents/invoices/etc.) — keep dialog open, show why.
        setDeleteError(message);
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  /** Reloads the units and returns them, or null if the request failed. */
  async function refresh(): Promise<AdminUnit[] | null> {
    setLoading(true);
    setError(null);
    try {
      const list = await listAdminUnits();
      setUnits(list);
      return list;
    } catch (e) {
      setError(extractApiError(e).message);
      return null;
    } finally {
      setLoading(false);
    }
  }

  // Residents and billing enrich the map but are optional: each can 403 for
  // staff without that permission, and the page still works without them.
  const refreshResidents = () => {
    listResidents().then(setResidents).catch(() => setResidents(null));
  };

  useEffect(() => {
    refresh();
    refreshResidents();
    getUnitBilling()
      .then((rows) => setBilling(new Map(rows.map((r) => [r.unit_id, r]))))
      .catch(() => setBilling(null));
  }, []);

  const changeView = (next: View) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Only a convenience; the view simply resets next time.
    }
  };

  const rows = useMemo(() => buildUnitRows(units, residents ?? []), [units, residents]);
  const counts = useMemo(() => filterCounts(rows), [rows]);
  const blocks = useMemo(() => [...new Set(units.map((u) => u.block))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [units]);
  const visible = (row: UnitRow) =>
    matchesFilter(row, filter) && matchesSearch(row, query) && (block === 'all' || row.unit.block === block);
  const groups = useMemo(() => groupByBlock(rows, visible), [rows, filter, query, block]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownRows = groups.flatMap((g) => g.rows);

  // "Unregistered" only means something once residents have loaded.
  const filters = FILTERS.filter((f) => f.key !== 'unregistered' || residents !== null);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={3}>
        <div>
          <Typography variant="h4" fontWeight={600}>
            {t('units.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('units.subtitleShort')}
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          {t('units.newUnit')}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {info ? (
        <Alert severity="info" onClose={() => setInfo(null)}>
          {info}
        </Alert>
      ) : null}

      {/* Counts that are also filters. */}
      <FilterChips
        chips={filters.map((f) => ({ key: f.key, label: t(f.label), count: counts[f.key] }))}
        value={filter}
        onChange={setFilter}
      />

      {/* Search and block on the left, the view switch on the right. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={canManageResidents ? t('units.searchWithResidents') : t('units.search')}
          sx={{ width: { xs: '100%', sm: 300 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField select size="small" value={block} onChange={(e) => setBlock(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">{t('units.blockAll')}</MenuItem>
          {blocks.map((b) => (
            <MenuItem key={b} value={b}>{b}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v: View | null) => v && changeView(v)}
          aria-label={t('units.viewLabel')}
        >
          <ToggleButton value="map" sx={{ gap: 0.75, px: 1.5, textTransform: 'none' }}>
            <GridViewIcon fontSize="small" />
            {t('units.viewMap')}
          </ToggleButton>
          <ToggleButton value="table" sx={{ gap: 0.75, px: 1.5, textTransform: 'none' }}>
            <TableRowsIcon fontSize="small" />
            {t('units.viewTable')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : units.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('units.empty')}</Typography>
          </CardContent>
        </Card>
      ) : shownRows.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('units.noMatch')}</Typography>
          </CardContent>
        </Card>
      ) : view === 'map' ? (
        <Stack spacing={1.5}>
          {residents !== null ? <BlockMapLegend /> : null}
          <BlockMap groups={groups} selectedId={selected?.id ?? null} onSelect={(row) => setSelected(row.unit)} />
        </Stack>
      ) : (
        <Card variant="outlined">
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('units.colBlock')}</TableCell>
                    <TableCell>{t('units.colNumber')}</TableCell>
                    <TableCell>{t('units.colLabel')}</TableCell>
                    <TableCell sx={{ width: 140 }}>{t('units.colType')}</TableCell>
                    <TableCell sx={{ width: 160 }}>{t('units.colOccupancy')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shownRows.map(({ unit: u }) => (
                    <TableRow key={u.id} hover onClick={() => setSelected(u)} sx={{ cursor: 'pointer' }}>
                      <TableCell>{u.block}</TableCell>
                      <TableCell>{u.number}</TableCell>
                      <TableCell>{u.label}</TableCell>
                      <TableCell>
                        <Chip size="small" label={t(UNIT_TYPE_LABELS[u.type] ?? u.type)} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <OccupancyChip occupancy={u.occupancy} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <UnitDrawer
        unit={selected}
        billing={billing ? billing.get(selected?.id ?? '') ?? null : null}
        canManageResidents={canManageResidents}
        onClose={() => setSelected(null)}
        onEdit={(u) => setEditing(u)}
        onDelete={(u) => {
          setDeleteError(null);
          setDeleting(u);
        }}
        onResidentsChanged={refreshResidents}
      />

      <UnitDialog
        open={creating}
        unit={null}
        existing={units}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          refresh();
        }}
        onImported={refresh}
      />
      <UnitDialog
        open={!!editing}
        unit={editing}
        existing={units}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          const id = editing?.id;
          setEditing(null);
          const list = await refresh();
          // Keep the panel open on the edited unit, with its new details.
          const fresh = list?.find((u) => u.id === id);
          if (fresh) setSelected((cur) => (cur?.id === fresh.id ? fresh : cur));
        }}
      />

      <Dialog
        open={!!deleting}
        onClose={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('units.deleteTitle', { label: deleting?.label })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('units.deleteWarning')}
            </Typography>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleteBusy}>
            {deleteBusy ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function OccupancyChip({ occupancy }: { occupancy: AdminUnit['occupancy'] }) {
  const t = useT();
  const color = occupancy === 'VACANT' ? 'default' : 'success';
  return <Chip size="small" label={t(OCCUPANCY_LABELS[occupancy] ?? occupancy)} color={color} />;
}

function UnitDialog({
  open,
  unit,
  existing,
  onClose,
  onSaved,
  onImported,
}: {
  open: boolean;
  unit: AdminUnit | null;
  /** Every unit in the cluster, so the import can spot ones that already exist. */
  existing: AdminUnit[];
  onClose: () => void;
  onSaved: () => void;
  onImported?: () => void;
}) {
  const t = useT();
  const isEdit = !!unit;
  // A new unit can be added one at a time or many at once from a spreadsheet.
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [block, setBlock] = useState('');
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState<(typeof UNIT_TYPES)[number]>('HOUSE');
  const [occupancy, setOccupancy] = useState<(typeof OCCUPANCIES)[number]>('OCCUPIED');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setMode('single');
    setError(null);
    setBlock(unit?.block ?? '');
    setNumber(unit?.number ?? '');
    setLabel(unit?.label ?? '');
    setType((unit?.type as (typeof UNIT_TYPES)[number]) ?? 'HOUSE');
    setOccupancy(unit?.occupancy === 'VACANT' ? 'VACANT' : 'OCCUPIED');
  }, [open, unit]);

  const canSave = isEdit ? true : block.trim() !== '' && number.trim() !== '';

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (isEdit && unit) {
        await updateUnit(unit.id, { label: label.trim(), type, occupancy });
      } else {
        await createUnit({
          block: block.trim(),
          number: number.trim(),
          ...(label.trim() ? { label: label.trim() } : {}),
          type,
          occupancy,
        });
      }
      onSaved();
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth={mode === 'bulk' ? 'sm' : 'xs'} fullWidth>
      <DialogTitle>{isEdit ? t('units.editTitle') : t('units.createTitle')}</DialogTitle>
      {isEdit ? null : (
        <Tabs value={mode} onChange={(_, v: 'single' | 'bulk') => setMode(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="single" label={t('units.tabSingle')} />
          <Tab value="bulk" label={t('units.tabBulk')} />
        </Tabs>
      )}
      {mode === 'bulk' ? (
        <UnitImportPanel existing={existing} onClose={onClose} onImported={() => onImported?.()} />
      ) : (
        <>
          <DialogContent>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label={t('units.block')}
                  value={block}
                  onChange={(e) => setBlock(e.target.value)}
                  disabled={isEdit}
                  required={!isEdit}
                  placeholder={t('units.blockPlaceholder')}
                  fullWidth
                />
                <TextField
                  label={t('units.number')}
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  disabled={isEdit}
                  required={!isEdit}
                  placeholder="18"
                  fullWidth
                />
              </Stack>
              <TextField
                label={t('units.labelOptional')}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('units.labelPlaceholder')}
                fullWidth
              />
              <TextField
                select
                label={t('units.type')}
                value={type}
                onChange={(e) => setType(e.target.value as (typeof UNIT_TYPES)[number])}
                fullWidth
              >
                {UNIT_TYPES.map((ut) => (
                  <MenuItem key={ut} value={ut}>
                    {t(UNIT_TYPE_LABELS[ut] ?? ut)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('units.occupancy')}
                value={occupancy}
                onChange={(e) => setOccupancy(e.target.value as (typeof OCCUPANCIES)[number])}
                helperText={t(OCCUPANCY_HELP[occupancy] ?? '')}
                fullWidth
              >
                {OCCUPANCIES.map((o) => (
                  <MenuItem key={o} value={o}>
                    {t(OCCUPANCY_LABELS[o] ?? o)}
                  </MenuItem>
                ))}
              </TextField>
              {isEdit ? (
                <Typography variant="caption" color="text.secondary">
                  {t('units.blockCannotEdit')}
                </Typography>
              ) : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button onClick={save} variant="contained" disabled={!canSave || busy}>
              {busy ? t('common.saving') : isEdit ? t('common.save') : t('common.create')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
